import { ReactElement, useEffect } from 'react';
import { useFormatDate } from '@/common/hooks';
import styles from './reports-page.module.scss';
import {
    UIDatePresetFilterItem,
    UIDateRangeFilterItem,
    UIFilterGroup,
    DateFilterEnum,
    useUINav,
    UIPeriodFilterItem
} from '@/ui';
import {
    DeviceFilterItem,
    NoAccessMask,
    TeamFilterItem,
    UserFilterItem
} from '@/tasks/components';
import { Navigate } from 'react-router';
import { useReportsFilter } from '@/analytics/providers';
import {
    ActivityTable,
    AlertTypePieChart,
    DayOfTheWeekActivityBarChart,
    HoursActiveGraph,
    LocationHeatmap,
    RealVsFalseAlertsPieChart,
    TasksAndAlertsBarChart,
    TotalAlertsChart,
    TotalRealAlertsChart,
    TotalTasksChart
} from '@/analytics/components';
import { useCompanyPermissions } from '@/companies/hooks';
import { usePagesTranslation } from '../hooks';

const ITEM_SPACING = 16;

export function ReportsPage(): ReactElement {
    const { t } = usePagesTranslation();
    const { setParts } = useUINav();
    useEffect(() => {
        setParts([{ text: t('shared.labels.reports') }]);
    }, [setParts, t]);

    const { canViewReports } = useCompanyPermissions();
    if (!canViewReports) {
        return <Navigate to="/" replace />;
    }

    return <Content />;
}

function Content(): ReactElement {
    const { formatDateRange } = useFormatDate();
    const { hasSubscription, hasLicense } = useCompanyPermissions();

    const { preset, range, timezone, period, setFilter, allowedPeriods } =
        useReportsFilter();

    return (
        <div className={styles.container}>
            <div
                style={{
                    display: 'flex',
                    flexShrink: 1,
                    gap: ITEM_SPACING
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        flex: 2
                    }}
                >
                    <UIFilterGroup>
                        <TeamFilterItem />
                        <UserFilterItem />
                        <DeviceFilterItem />
                    </UIFilterGroup>
                    <UIFilterGroup>
                        <UIDatePresetFilterItem
                            timeType={preset}
                            onDateChange={(_timeType) => {
                                if (_timeType === DateFilterEnum.Custom) {
                                    setFilter({
                                        date: {
                                            preset: DateFilterEnum.Custom,
                                            range
                                        }
                                    });
                                } else {
                                    setFilter({
                                        date: {
                                            preset: _timeType,
                                            range
                                        }
                                    });
                                }
                            }}
                        />
                        {preset !== DateFilterEnum.AllTime && (
                            <UIDateRangeFilterItem
                                label={formatDateRange(range.from, range.to)}
                                from={range.from}
                                to={range.to}
                                timezone={timezone}
                                onDateChange={(from, to) => {
                                    setFilter({
                                        date: {
                                            preset: DateFilterEnum.Custom,
                                            range: { from: from, to: to }
                                        }
                                    });
                                }}
                            />
                        )}
                        <UIPeriodFilterItem
                            periodType={period}
                            onPeriodChange={(period) => {
                                setFilter({
                                    period
                                });
                            }}
                            options={allowedPeriods}
                        />
                    </UIFilterGroup>
                </div>
            </div>
            <div className={styles.graphGrid}>
                <div className={styles.graphRow}>
                    <TotalTasksChart />
                    <TotalAlertsChart />
                    <TotalRealAlertsChart />
                </div>

                <div className={styles.graphRow}>
                    <div className={styles.twoColumnSpan}>
                        <TasksAndAlertsBarChart />
                    </div>

                    <ActivityTable />
                </div>

                <div className={styles.graphRow}>
                    <div className={styles.twoColumnSpan}>
                        <DayOfTheWeekActivityBarChart />
                    </div>
                    <HoursActiveGraph />
                </div>
                <div className={styles.graphRow}>
                    <div className={styles.twoColumnSpan}>
                        {/* <MiniLocationHeatmap /> */}
                        <LocationHeatmap />
                    </div>
                    <div className={styles.pieChartWrapper}>
                        <RealVsFalseAlertsPieChart />
                        <AlertTypePieChart />
                    </div>
                </div>
            </div>

            {!hasSubscription && <NoAccessMask withMessage="no-subscription" />}
            {hasSubscription && !hasLicense && (
                <NoAccessMask withMessage="no-license" />
            )}
        </div>
    );
}
